import * as THREE from 'three';
import * as Comlink from 'comlink';
import SlicingWorker from '../workers/slicing.worker.js?worker';

const _worldToLocal = new THREE.Matrix4();
const _localPlane = new THREE.Plane();

let workerApiPromise = null;

function getWorkerApi() {
  if (!workerApiPromise) {
    workerApiPromise = (async () => {
      const worker = new SlicingWorker();
      return Comlink.wrap(worker);
    })();
  }
  return workerApiPromise;
}

/**
 * Spawn the slicing worker ahead of the first slice.
 *
 * Worker spawn plus its module-graph load currently lands inside the first apply,
 * where the user is waiting on it. Calling this when the tool opens overlaps it
 * with them positioning the attachment. Idempotent — `getWorkerApi` caches the
 * promise, so the slice path awaits this same one.
 */
export function prewarmSlicingWorker() {
  return getWorkerApi();
}

export function worldPlaneToMeshLocal(worldPlane, mesh, target = new THREE.Plane()) {
  mesh.updateMatrixWorld(true);
  _worldToLocal.copy(mesh.matrixWorld).invert();
  return target.copy(worldPlane).applyMatrix4(_worldToLocal);
}

/**
 * Expand one indexed attribute into the flat per-corner array the worker wants.
 *
 * This is three.js's own `convertBufferAttribute` loop from `toNonIndexed()`,
 * written straight into a `Float32Array` destination:
 * `out[i * itemSize + k] = src[index[i] * itemSize + k]`. Values are therefore
 * identical, including the float64→float32 narrowing three.js would have done
 * one step later when the caller copied the expanded attribute out.
 */
function deindexAttribute(attr, idx) {
  const src = attr.array;
  const itemSize = attr.itemSize;
  const out = new Float32Array(idx.length * itemSize);
  for (let i = 0; i < idx.length; i++) {
    const s = idx[i] * itemSize;
    const d = i * itemSize;
    for (let k = 0; k < itemSize; k++) out[d + k] = src[s + k];
  }
  return out;
}

/**
 * Slice shaded mesh in a worker. Plane is in world space.
 * @returns {Promise<{ upper: object, lower: object } | null>}
 */
export async function sliceShadedMeshAsync(shadedMesh, worldPlane) {
  if (!shadedMesh?.geometry || !worldPlane) return null;

  const geom = shadedMesh.geometry;
  const posAttr = geom.getAttribute('position');
  if (!posAttr) return null;
  const colorAttr = geom.getAttribute('color');

  // The worker needs a triangle soup (`performSlice` walks positions in threes).
  //
  // `toNonIndexed()` produced one, but expensively: it builds a whole
  // BufferGeometry and expands EVERY attribute — normals and uvs included, which
  // the worker never reads — and then the caller copied position and colour out
  // of it into fresh arrays anyway. On the ~1.08 M-triangle socket that is
  // roughly 260 MB allocated to deliver 78 MB, plus a geometry to dispose.
  //
  // Deindexing straight into the destination does the same arithmetic once.
  const idx = geom.index?.array;
  const positions = idx ? deindexAttribute(posAttr, idx) : new Float32Array(posAttr.array);
  let colors = null;
  if (colorAttr) {
    colors = idx ? deindexAttribute(colorAttr, idx) : new Float32Array(colorAttr.array);
  }

  worldPlaneToMeshLocal(worldPlane, shadedMesh, _localPlane);

  const api = await getWorkerApi();
  // Transfer rather than structured-clone. Comlink copies arguments by default,
  // so these ~78 MB were being duplicated a second time crossing the boundary.
  // Neutering is safe: both buffers were allocated above purely to be sent, and
  // nothing reads them after this call. (The worker already transfers its
  // results back — see the Comlink.transfer at the end of slicing.worker.js.)
  return api.sliceMesh(
    Comlink.transfer(positions, [positions.buffer]),
    colors ? Comlink.transfer(colors, [colors.buffer]) : null,
    _localPlane.normal.toArray(),
    _localPlane.constant,
  );
}

/**
 * Build an indexed BufferGeometry from one side of a worker slice payload.
 * @param {{positions, normals, colors, indices}} data One of result.upper / result.lower
 * @returns {THREE.BufferGeometry | null}
 */
export function buildGeometryFromSliceData(data) {
  if (!data?.positions?.length) return null;

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
  if (data.indices?.length) geom.setIndex(new THREE.BufferAttribute(data.indices, 1));
  if (data.normals?.length) geom.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  if (data.colors?.length) geom.setAttribute('color', new THREE.BufferAttribute(data.colors, 3));
  return geom;
}

/**
 * Slice a mesh with the Slice tool's worker algorithm and return the geometry
 * on the POSITIVE side of the world plane (the half the plane normal points
 * toward), baked into WORLD space.
 *
 * This is the exact slice path the Slice tool uses (true plane-clip that splits
 * straddling triangles along the cut), exposed for the locking assembly steps
 * so they cut the socket the same way the tool does instead of with a
 * centroid-side filter. The cut is OPEN (no cap disk added), matching what the
 * locking pipeline consumes.
 *
 * @param {THREE.Mesh} mesh Mesh to slice (e.g. the socket).
 * @param {THREE.Plane} worldPlane Cut plane in world space.
 * @returns {Promise<THREE.BufferGeometry | null>} World-space geometry of the
 *   positive side, or null if the slice produced nothing.
 */
export async function sliceMeshKeepPositiveSideWorldAsync(mesh, worldPlane) {
  const result = await sliceShadedMeshAsync(mesh, worldPlane);
  // sliceShadedMeshAsync slices in mesh-local space; "upper" is the side the
  // (local) plane normal points toward, which is preserved through the world
  // transform => the positive side of worldPlane.
  const geom = buildGeometryFromSliceData(result?.upper);
  if (!geom) return null;

  mesh.updateMatrixWorld(true);
  if (mesh.matrixWorld) geom.applyMatrix4(mesh.matrixWorld); // transforms positions + normals
  geom.computeBoundingBox();
  geom.computeBoundingSphere();
  return geom;
}

/**
 * Build a mesh from worker slice payload (indexed geometry).
 */
export function createMeshFromSliceData(data, name, sourceMesh, material) {
  if (!data?.positions?.length) return null;

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));

  if (data.indices?.length) {
    geom.setIndex(new THREE.BufferAttribute(data.indices, 1));
  }
  if (data.normals?.length) {
    geom.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  }
  if (data.colors?.length) {
    geom.setAttribute('color', new THREE.BufferAttribute(data.colors, 3));
  }

  geom.computeBoundingBox();
  geom.computeBoundingSphere();

  const mesh = new THREE.Mesh(geom, material);
  mesh.name = name;
  mesh.matrix.copy(sourceMesh.matrix);
  mesh.matrixAutoUpdate = sourceMesh.matrixAutoUpdate;
  return mesh;
}
